/**
 * CAIQ Page - Cloud Security Alliance Questionnaire Responses
 * With inline editing capability
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Download, Globe, X, Edit2, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, Badge } from '../components/ui-custom/ComplianceUI';
import SearchBar from '../components/common/SearchBar';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function CAIQEditModal({ item, onClose, onSave, saving }) {
  const [answer, setAnswer] = useState(item.answer || 'Yes');
  const [responseText, setResponseText] = useState(item.response_text || '');

  const handleSave = () => {
    onSave({
      answer,
      response_text: responseText,
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content caiq-editor" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-control-id" style={{ color: '#2563eb' }}>{item.question_id || item.control_id}</div>
            <h2>Edit CAIQ Response</h2>
          </div>
          <button className="modal-close" onClick={onClose} data-testid="close-caiq-modal">
            <X size={20} />
          </button>
        </div>
        
        <div className="modal-body">
          {/* Question */}
          <div className="control-detail-section">
            <h3>Question</h3>
            <p className="caiq-question-text">{item.question_text}</p>
          </div>

          {/* Domain Info */}
          <div className="control-detail-section">
            <h3>Domain</h3>
            <p>{item.domain_name || item.domain}</p>
          </div>

          {/* Answer Selection */}
          <div className="control-detail-section">
            <h3>Answer</h3>
            <div className="answer-options">
              {['Yes', 'No', 'N/A', 'Partial'].map(opt => (
                <button
                  key={opt}
                  onClick={() => setAnswer(opt)}
                  className={`answer-option ${answer === opt ? 'selected' : ''}`}
                  style={{ 
                    borderColor: answer === opt ? (opt === 'Yes' ? '#059669' : opt === 'No' ? '#dc2626' : '#6b7280') : 'transparent',
                    background: answer === opt ? (opt === 'Yes' ? '#05966920' : opt === 'No' ? '#dc262620' : '#6b728020') : 'var(--card-bg)'
                  }}
                  data-testid={`answer-${opt.toLowerCase()}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Response Text */}
          <div className="control-detail-section">
            <h3>Response Details</h3>
            <textarea
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              placeholder="Provide detailed response explaining how Eyvo addresses this requirement..."
              className="control-textarea"
              rows={6}
              data-testid="caiq-response-text"
            />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button 
            className="btn-primary" 
            onClick={handleSave}
            disabled={saving}
            data-testid="save-caiq-btn"
          >
            {saving ? <Loader2 className="spin" size={16} /> : <CheckCircle2 size={16} />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CAIQPage() {
  const [responses, setResponses] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchCAIQ = useCallback(async () => {
    try {
      
      const response = await fetch(`${API_URL}/api/caiq`, {
        credentials: 'include',
        
      });
      if (response.ok) {
        const data = await response.json();
        setDomains(data.domains || []);
        // Flatten all responses
        const allResponses = [];
        (data.domains || []).forEach(d => {
          (d.controls || []).forEach(c => {
            allResponses.push({ ...c, domain: d.domain, domain_name: d.display_name });
          });
        });
        setResponses(allResponses);
      }
    } catch (error) {
      console.error('Failed to fetch CAIQ:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCAIQ();
  }, [fetchCAIQ]);

  const handleExport = async (format) => {
    try {
      const response = await fetch(
        `${API_URL}/api/caiq/export?format=${format}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        toast.error('Export failed');
        return;
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eyvo_caiq_responses.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Exported CAIQ responses as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    }
  };

  const handleEditClick = (item) => {
    setSelectedItem(item);
    setEditModalOpen(true);
  };

  const handleSaveCAIQ = async (updates) => {
    if (!selectedItem) return;
    setSaving(true);
    
    try {
      
      const questionId = selectedItem.question_id || selectedItem.control_id;
      
      const response = await fetch(`${API_URL}/api/caiq/${encodeURIComponent(questionId)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          
        },
        credentials: 'include',
        body: JSON.stringify(updates),
      });
      
      if (response.ok) {
        toast.success(`CAIQ response updated`);
        setEditModalOpen(false);
        fetchCAIQ(); // Refresh the list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update CAIQ response');
      }
    } catch (error) {
      toast.error('Failed to update CAIQ response');
    } finally {
      setSaving(false);
    }
  };

  const filteredResponses = (() => {
    const base = selectedDomain === 'all'
      ? responses
      : responses.filter((r) => r.domain === selectedDomain);
    const q = searchQuery.trim().toLowerCase();
    if (!q) return base;
    const tokens = q.split(/\s+/).filter(Boolean);
    return base.filter((r) => {
      const hay = [
        r.question_id || '',
        r.control_id || '',
        r.domain || '',
        r.question || '',
        r.response_text || '',
        r.answer || '',
      ].join(' ').toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  })();

  return (
    <div className="caiq-page">
      <div className="page-header">
        <div>
          <h1>CAIQ v4.1.0 Responses</h1>
          <p>Cloud Security Alliance Questionnaire — {responses.length} responses across {domains.length} domains. Click any response to edit.</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => handleExport('csv')} data-testid="export-caiq-csv">
            <Download size={16} /> Export CSV
          </button>
          <button className="btn-secondary" onClick={() => handleExport('json')} data-testid="export-caiq-json">
            <Download size={16} /> Export JSON
          </button>
        </div>
      </div>

      {/* Search */}
      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder='Find a question — try "encryption", "CCM-08", or "IAM-01"'
        totalCount={responses.length}
        filteredCount={filteredResponses.length}
        testIdPrefix="caiq-search"
      />

      {/* Domain Filter */}
      <div className="filter-bar">
        <button
          onClick={() => setSelectedDomain('all')}
          className={`filter-btn ${selectedDomain === 'all' ? 'active' : ''}`}
        >
          All Domains
        </button>
        {domains.map(d => (
          <button
            key={d.domain}
            onClick={() => setSelectedDomain(d.domain)}
            className={`filter-btn ${selectedDomain === d.domain ? 'active' : ''}`}
          >
            {d.domain} ({d.controls?.length || 0})
          </button>
        ))}
      </div>

      {/* Responses List */}
      {loading ? (
        <div className="loading-container"><div className="spinner" /></div>
      ) : filteredResponses.length > 0 ? (
        <div className="caiq-list">
          {filteredResponses.map((item, i) => (
            <Card 
              key={item.question_id || i} 
              className="caiq-card clickable"
              onClick={() => handleEditClick(item)}
              data-testid={`caiq-${item.question_id || item.control_id}`}
            >
              <div className="caiq-header">
                <div className="caiq-id">{item.question_id || item.control_id}</div>
                <div className="caiq-actions">
                  <button 
                    className="edit-btn"
                    onClick={(e) => { e.stopPropagation(); handleEditClick(item); }}
                    data-testid={`edit-caiq-${item.question_id || item.control_id}`}
                  >
                    <Edit2 size={14} />
                  </button>
                  <Badge color={item.answer === 'Yes' ? '#059669' : item.answer === 'No' ? '#dc2626' : '#6b7280'}>
                    {item.answer || 'Yes'}
                  </Badge>
                </div>
              </div>
              <div className="caiq-question">{item.question_text}</div>
              {item.response_text && (
                <div className="caiq-response">{item.response_text}</div>
              )}
              <div className="caiq-domain">Domain: {item.domain_name || item.domain}</div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="empty-state">
          <Globe size={48} color="#6b7280" />
          <h3>No CAIQ Responses</h3>
          <p>CAIQ v4.1.0 responses will appear here once configured.</p>
        </Card>
      )}

      {/* Edit Modal */}
      {editModalOpen && selectedItem && (
        <CAIQEditModal
          item={selectedItem}
          onClose={() => setEditModalOpen(false)}
          onSave={handleSaveCAIQ}
          saving={saving}
        />
      )}
    </div>
  );
}
